import React, { useMemo, useState, useCallback, ReactNode } from "react"
import { Link } from "@reach/router"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconLookup } from "@fortawesome/fontawesome-svg-core"
import { TFunction } from "i18next"
import { ReactComponent as Logo } from "assets/svg/logo.svg"
import { faBars, faTimes } from "@fortawesome/pro-solid-svg-icons"
import { useTranslation } from "react-i18next"

import { useUserContext } from "../../../context/UserContext"
import { IdentityFieldsFragment } from "../../../generated/graphql-operations"

import Styles from "./Menu.module.css"

export interface MenuItem {
	id: string
	icon: IconLookup
	to: string
	title: string
	description?: ReactNode
	exact?: boolean
	items?: MenuItem[]
}

export type MenuFactory = (
	t: TFunction,
	user?: IdentityFieldsFragment
) => MenuItem

const traverseChildren = (acc: string[], value: MenuItem): string[] => {
	const children = (value.items || []).reduce(traverseChildren, [])
	children.push(value.to)

	return acc.concat(children)
}

const RenderMenuItem: React.FC<{
	item: MenuItem
	baseKey?: string
	onClick?: () => void
}> = ({ baseKey, item, onClick }) => {
	const childHrefs = useMemo(() => {
		return traverseChildren([], item)
	}, [item])

	return (
		<li className={Styles.menuItem}>
			<Link
				className={Styles.menuItemLink}
				getProps={({
					isCurrent,
					isPartiallyCurrent,
					location: { pathname },
				}) => {
					return {
						className: `${Styles.menuItemLink} ${
							isCurrent ||
							(!item.exact &&
								(isPartiallyCurrent ||
									childHrefs.includes(pathname)))
								? Styles.menuItemLinkActive
								: ""
						}`,
					}
				}}
				title={
					typeof item.description === "string"
						? item.description
						: undefined
				}
				key={`main-menu-item-${item.id}`}
				to={item.to}
				onClick={onClick}
			>
				<FontAwesomeIcon
					className={Styles.menuItemIcon}
					icon={item.icon}
					aria-hidden
				/>
				<span>{item.title}</span>
			</Link>
			{item.items && (
				<RenderMenu
					baseKey={`${baseKey}-${item.id}`}
					items={item.items}
					onClick={onClick}
				/>
			)}
		</li>
	)
}

const RenderMenu: React.FC<{
	baseKey?: string
	items: MenuItem[]
	onClick?: () => void
}> = ({ items, baseKey = "", onClick }) => {
	return (
		<ul className={Styles.menuItems}>
			{items.map((item) => {
				return (
					<RenderMenuItem
						baseKey={baseKey}
						item={item}
						key={`main-menu-item-${baseKey}-${item.id}`}
						onClick={onClick}
					/>
				)
			})}
		</ul>
	)
}

const Menu: React.FC<{
	factories: MenuFactory[]
}> = ({ factories }) => {
	const [t] = useTranslation()
	const { user } = useUserContext()
	const items = useMemo(() => {
		return factories.map((f) => f(t, user))
	}, [factories, t, user])

	const [menuOpened, setMenuOpened] = useState<boolean>()

	const triggerMenu = useCallback(() => {
		setMenuOpened((menuOpened) => !menuOpened)
	}, [])

	const onCloseMenu = useCallback(() => {
		setMenuOpened(false)
	}, [])

	return (
		<aside
			className={`${Styles.menu} ${Styles.menuAnimation} ${
				menuOpened ? "" : Styles.mobileMenuClosed
			}`}
		>
			<header
				className={`${Styles.menuHeader} ${!menuOpened ? "pr-16" : ""}`}
			>
				<Link to="/" className="m-auto">
					<Logo className={Styles.menuLogo} />
				</Link>
				<button className={Styles.menuBars} onClick={triggerMenu}>
					{menuOpened ? (
						<FontAwesomeIcon icon={faTimes} />
					) : (
						<FontAwesomeIcon icon={faBars} />
					)}
				</button>
			</header>
			<nav className={Styles.nav}>
				<RenderMenu items={items} onClick={onCloseMenu} />
			</nav>
		</aside>
	)
}

export default Menu
